import { FlyoutOnboardingSectionName, LockMetadata, MicrosoftOnboarding, Sections, SellerTypeValue, TestConnectionStatusType } from '@labrav/common'

export type {
  FlyoutCloudSettings,
  FlyoutLabraPartnerDetails,
  FlyOutOnboardingGeneralInformation,
  FlyOutOnboardingLandingPageDetails,
  FlyOutOnboardingTerm,
  FlyOutOnboardingTermsAndConditions,
  ListingType,
  LockMetadata,
  MicrosoftOnboarding,
  Sections,
  SellerTypeValue,
  TeamMembers,
  TestConnectionStatusType,
} from '@labrav/common'
export { FlyoutOnboardingSectionName } from '@labrav/common'

export type KeyValue = {
  Text: string
  Url: string | boolean | number | null | undefined
}

export enum FlyoutOnboardingForm {
  SET_FLYOUT_ONBOARDING_FORM = 'SET_FLYOUT_ONBOARDING_FORM',
  UPDATE_FLYOUT_ONBOARDING_FORM = 'UPDATE_FLYOUT_ONBOARDING_FORM',
  UPDATE_FLYOUT_TEST_CONNECTION_STATUS = 'UPDATE_FLYOUT_TEST_CONNECTION_STATUS',
  UPDATE_FLYOUT_CAS_TEST_CONNECTION_STATUS = 'UPDATE_FLYOUT_CAS_TEST_CONNECTION_STATUS',
  UPDATE_MICROSOFT_ONBOARDING_FORM = 'UPDATE_MICROSOFT_ONBOARDING_FORM',
}

export type FlyOutOnboardingChangeType = {
  sectionName: FlyoutOnboardingSectionName
  keyName: string
  value: string | boolean
}

export enum FlyoutOnboardingPartnershipType {
  ISV = 'ISV',
  CP = 'CP',
  ISV_AND_CP = 'ISV_AND_CP', // this just gets stored in draft json
}

export enum AWSPartnershipType {
  TECHNOLOGY_PARTNER = 'TECHNOLOGY_PARTNER',
  CONSULTING_PARTNER = 'CONSULTING_PARTNER',
}

export type TestConnectionStatusProps = {
  status: TestConnectionStatusType
  error?: string
}

export interface FlyOutOnboarding {
  onboardingForm: Sections
  formChanged: boolean
  testConnectionStatus: TestConnectionStatusProps
  microsoftOnboardingForm: MicrosoftOnboarding
}

export type SendFlyoutOnboardingProps = {
  isAutoSave: boolean
  onboardingForm: Sections
  pageMarkerMapping: Record<string, string>
  page: FlyoutOnboardingSectionName
  partnerId: string
  tenantPartnerId: string
  status?: string
  callBackFlyoutOnboarding?: () => void
  isOnboarded?: boolean
}

export type ProcessFlyoutOnboardingParams = {
  isAutoSave: boolean
  onboardingForm: Sections
  pageMarkerMapping: Record<string, string>
  page: FlyoutOnboardingSectionName
  status?: string
  isFormUpdated?: boolean
  partnerId: string
}

export type CheckAllCompletedParams = {
  page: FlyoutOnboardingSectionName
  pageMarker: Record<string, boolean>
  isManaged?: boolean
}

export type CheckMicrosoftConnectionPayload = {
  client_id: string
  client_secret: string
  tenant_id: string
  mpn_id: string
}

export enum MicrosoftCloudSettingsSections {
  PREREQUISITES = 'prerequisites',
  ACCOUNT_CONFIGURATION = 'accountConfiguration',
  CLOUD_CREDENTIALS = 'cloudCredentials',
  STEPS = 'steps',
  LOCK_METADATA = 'lockMetadata',
  FILLED_CLOUD_SETTINGS = 'filledCloudSettings',
  FILLED_COMPANY_INFO = 'filledCompanyInfo',
  COMPANY_INFO = 'companyInfo',
  SUBMIT = 'submit',
}

export interface Prerequisites {
  hasAccessedMpc: boolean
  hasEnrolledInCommercialMarketplace: boolean
  hasCompletedOrganizationProfile: boolean
}

export interface PrerequisitesArrayItem {
  id: string
  value: boolean
}

export type PrerequisitesArray = PrerequisitesArrayItem[]

export interface AccountConfiguration {
  sellerType: SellerTypeValue[]
  currencies: string[]
}

export interface CloudCredentials {
  connectionName: string
  tenantId: string
  clientId: string
  clientSecret: string
  clientSecretExpirationDate: string
  mpnId: string
  connectionTested?: TestConnectionStatusType
}

export interface MicrosoftCloudSettingsSteps {
  prerequisites: boolean
  accountConfiguration: boolean
  cloudCredentials: boolean
}

export interface CompanyInfo {
  companyName: string
  companyIndustry: string
  companyWebsite: string
  acceptedTermsAndConditions: boolean
}

export type FlyoutCloudSettingsSectionsProps = {
  [MicrosoftCloudSettingsSections.PREREQUISITES]: Prerequisites
  [MicrosoftCloudSettingsSections.ACCOUNT_CONFIGURATION]: AccountConfiguration
  [MicrosoftCloudSettingsSections.CLOUD_CREDENTIALS]: CloudCredentials
  [MicrosoftCloudSettingsSections.STEPS]: MicrosoftCloudSettingsSteps
  [MicrosoftCloudSettingsSections.LOCK_METADATA]: LockMetadata
  [MicrosoftCloudSettingsSections.FILLED_CLOUD_SETTINGS]: boolean
  [MicrosoftCloudSettingsSections.FILLED_COMPANY_INFO]: boolean
  [MicrosoftCloudSettingsSections.COMPANY_INFO]: CompanyInfo
  [MicrosoftCloudSettingsSections.SUBMIT]: boolean
}

export const initialState: FlyOutOnboarding = {
  onboardingForm: {} as Sections,
  microsoftOnboardingForm: {
    hasAccessedMpc: false,
    hasEnrolledInCommercialMarketplace: false,
    hasCompletedOrganizationProfile: false,
    sellerType: [],
    currencies: ['USD'] as any,
    connectionName: '',
    tenantId: '',
    clientId: '',
    clientSecret: '',
    clientSecretExpirationDate: '',
    mpnId: '',
    connectionTested: 'standBy',
    lockMetadata: {
      lockedBy: '',
      lastActivityAt: '',
      lockAcquisitionProbe: false,
      windowId: '',
      editingEnabled: false,
      lockedByName: '',
      lockByEmail: '',
    },
    filledCloudSettings: false,
    filledCompanyInfo: false,
    companyName: '',
    companyIndustry: '',
    companyWebsite: '',
    acceptedTermsAndConditions: false,
    submit: false,
  } as MicrosoftOnboarding,
  formChanged: false,
  testConnectionStatus: {
    status: 'standBy',
  },
}

export type FlyOutOnboardingAction =
  | {
      type: FlyoutOnboardingForm.SET_FLYOUT_ONBOARDING_FORM
      payload: Sections
    }
  | {
      type: FlyoutOnboardingForm.UPDATE_FLYOUT_ONBOARDING_FORM
      payload: FlyOutOnboardingChangeType
    }
  | {
      type: FlyoutOnboardingForm.UPDATE_FLYOUT_TEST_CONNECTION_STATUS
      payload: TestConnectionStatusProps
    }
  | {
      type: FlyoutOnboardingForm.UPDATE_MICROSOFT_ONBOARDING_FORM
      payload: MicrosoftOnboarding
    }
